import React from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Briefcase, Users, ListChecks, Settings as SettingsIcon,
  UserCog, Building2, ShieldCheck, ShieldAlert, CircleDot, Circle, Inbox, PieChart,
  Menu, X, Receipt, ClipboardList,
} from 'lucide-react';
import ChatWidget from './shared/components/ChatWidget';
import { RefreshProvider } from './shared/contexts/RefreshContext';
import { AlertProvider } from './shared/contexts/AlertContext';
import { AuthProvider, useAuth, Login, AuthCallback } from './modules/auth';
import { Dashboard } from './modules/dashboard';
import { JobsList, JobDetail, CreateJob } from './modules/jobs';
import { CandidatesList, CandidateDetail } from './modules/candidates';
// import { InterviewRoom, VideoInterview } from './modules/interviews';
import { Shortlist } from './modules/shortlist';
import { PortalApply, PortalInterview, PortalMeeting, InterviewThankYou, EmbedJobBoard, SharedJob } from './modules/portal';
import { SttLab } from './modules/stt-lab';
import { CodeLab } from './modules/code-lab';
// import { PortalJobs, PortalSelfSchedule } from './modules/portal';
import { Settings, UsageCost, JobCosts, RechargeOrders, Profile, ChangePassword } from './modules/settings';
import { Companies, PlatformDashboard, SuperAdmins, DemoRequests, ImpersonateHandoff, WalletReport, AiCharges, PlatformSettings } from './modules/platform';
import { Team } from './modules/admin';
import { canCreateJob } from './shared/utils/permissions';
import './App.css';
// Narrow-viewport layout adjustments. Loaded after App.css so it overrides it.
import './responsive.css';
import ImpersonationBanner from './modules/platform/components/ImpersonationBanner';
import LowBalanceModal from './shared/components/LowBalanceModal';
import { BrandMark, BrandWordmark } from './shared/components/BrandLogo';

// Recruiting — everyone attached to a company sees these.
const NAV_ITEMS = [
  { path: '/',           label: 'Dashboard',  icon: LayoutDashboard },
  { path: '/jobs',       label: 'Job Posts',  icon: Briefcase },
  { path: '/candidates', label: 'Candidates', icon: Users },
  { path: '/shortlist',  label: 'Shortlist',  icon: ListChecks },
  // { path: '/usage',      label: 'Usage & Cost', icon: Wallet },
  { path: '/settings',   label: 'Settings',   icon: SettingsIcon },
];

// Managing people in your own company — admin, manager, and a team lead's own recruiters.
const TEAM_NAV_ITEMS = [
  { path: '/team', label: 'Manage Team', icon: UserCog },
];

// Cost by job stays in the app. Turn this on to show it in the sidebar again.
const SHOW_JOB_COSTS = false;

// Platform tier — super admin only. A super admin operates the service, they
// don't recruit, so this replaces the recruiting nav rather than adding to it.
const PLATFORM_NAV_ITEMS = [
  { path: '/',                      label: 'Overview',     icon: LayoutDashboard },
  { path: '/platform/companies',    label: 'Companies',    icon: Building2 },
  { path: '/platform/super-admins', label: 'Super Admins', icon: ShieldCheck },
  { path: '/platform/demo-requests', label: 'Demo Requests', icon: Inbox },
  { path: '/platform/wallet-report', label: 'Wallet profit', icon: PieChart },
  { path: '/platform/settings',     label: 'Settings',     icon: SettingsIcon },
];

export const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin:       'Admin',
  manager:     'Manager',
  team_lead:   'Team Lead',
  recruiter:   'Recruiter',
};

/** Anyone whose role can hand out at least one other role. */
const canManageTeam = (user) => Boolean(user?.company_id) && (user?.assignable_roles?.length > 0);
const isSuperAdmin  = (user) => user?.role === 'super_admin';
const isAdmin       = (user) => user?.role === 'admin';

// Whether the sidebar is showing the recruiting app or a management view.
// Lives in context so the root route can pick the matching dashboard.
const VIEW_MODE_KEY = 'recruit_view_mode';

/**
 * The view-mode preference is stored per user id.
 *
 * It used to live under one shared key, and localStorage is shared by every
 * account that signs in on a browser. So a company admin who chose "recruiting"
 * left that choice behind for the next person — and if that was a super admin,
 * who has no company, they landed in the recruiting view with no workspace to
 * show. Keying it by user, and ignoring it entirely for an account with no
 * company, closes both halves of that.
 */
function viewModeKeyFor(user) {
  return user?.id ? `${VIEW_MODE_KEY}:${user.id}` : null;
}

function readViewMode(user) {
  const key = viewModeKeyFor(user);
  if (!key) return false;
  try {
    return localStorage.getItem(key) === 'recruiter';
  } catch {
    return false;   // private mode or blocked storage — fall back to management
  }
}

function clearViewMode(user) {
  try {
    const key = viewModeKeyFor(user);
    if (key) localStorage.removeItem(key);
    localStorage.removeItem(VIEW_MODE_KEY);   // the old shared key
  } catch { /* nothing to clean up if storage is unavailable */ }
}
const ViewModeContext = React.createContext({ recruiterView: false, setRecruiterView: () => {} });
const useViewMode = () => React.useContext(ViewModeContext);

// ── Company Avatar ────────────────────────────────────────────────────────────
function CompanyAvatar({ user }) {
  const initials = (user.name || user.email || '?')
    .split(' ')
    .map(w => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div style={{
      width: 32, height: 32, borderRadius: 8,
      background: 'linear-gradient(135deg, #58a6ff, #a78bfa)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 13, fontWeight: 700, color: '#fff', flexShrink: 0,
      overflow: 'hidden',
    }}>
      {user.avatar_url
        ? <img src={user.avatar_url} alt={user.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : initials}
    </div>
  );
}

// ── Protected Route ───────────────────────────────────────────────────────────
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!loading && !user) navigate('/login', { replace: true });
  }, [loading, user, navigate]);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--bg-primary, #0d1117)' }}>
      <div style={{ color: 'var(--text-muted, #888)', fontSize: 14 }}>Loading…</div>
    </div>
  );
  if (!user) return null;
  return children;
}

// ── Role Route ────────────────────────────────────────────────────────────────
// Convenience only — the real gate is require_role / require_super_admin on the
// server, which reads the role from the live DB row on every request.
function RoleRoute({ allow, children, label = 'You don’t have permission to view this page.' }) {
  const { user } = useAuth();
  if (!user || !allow(user)) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon"><ShieldAlert size={40} strokeWidth={1.5} /></div>
        <div className="empty-state-title">Access denied</div>
        <div className="empty-state-text">{label}</div>
      </div>
    );
  }
  return children;
}

/**
 * Shown when a platform (super admin) account opens a company-scoped page.
 *
 * Says what happened and nothing more. It used to print the seeded demo
 * logins and their passwords, which shipped working credentials to anyone who
 * could reach this screen — including, on a seeded deployment, real ones.
 */
function NoCompanyState() {
  return (
    <div className="empty-state">
      <div className="empty-state-icon"><Building2 size={40} strokeWidth={1.5} /></div>
      <div className="empty-state-title">No company attached</div>
      <div className="empty-state-text">
        This is a platform account, so it has no jobs or candidates of its own.
        Open a company from the Companies page, or sign in with a company account.
      </div>
    </div>
  );
}

function CompanyRequiredRoute({ children }) {
  const { user } = useAuth();
  if (!user?.company_id) return <NoCompanyState />;
  return children;
}

// Posting a job is for a recruiter, or a team lead assigning it to one.
function JobCreatorRoute({ children }) {
  const { user } = useAuth();
  if (!canCreateJob(user)) return <Navigate to="/jobs" replace />;
  return children;
}

// ── Home ──────────────────────────────────────────────────────────────────────
// A super admin operates the platform and has no pipeline of their own, so the
// root route shows them platform totals instead of a recruiting dashboard.
function HomeRoute() {
  const { user } = useAuth();
  const { recruiterView } = useViewMode();

  if (isSuperAdmin(user) && !recruiterView) return <PlatformDashboard />;
  if (!user?.company_id) return <NoCompanyState />;
  return <Dashboard />;
}

/**
 * Off-canvas nav drawer, for viewports too narrow for the fixed sidebar.
 *
 * Open/closed is the only state; whether the drawer or the permanent sidebar is
 * on screen is decided in CSS at the 900px breakpoint. So a desktop user never
 * sees this — the class is applied either way and simply has no effect there.
 */
function useNavDrawer() {
  const [open, setOpen] = React.useState(false);
  const { pathname } = useLocation();

  // Navigating is the drawer's whole purpose, so arriving somewhere closes it.
  React.useEffect(() => { setOpen(false); }, [pathname]);

  // Escape closes, and the page behind the drawer must not scroll with it.
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    document.body.classList.add('nav-open');
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('nav-open');
    };
  }, [open]);

  // A widened window leaves the drawer open over a sidebar that is now
  // permanent, so close it once the breakpoint no longer applies.
  React.useEffect(() => {
    const mq = window.matchMedia('(min-width: 901px)');
    const onChange = (e) => { if (e.matches) setOpen(false); };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return [open, setOpen];
}

// ── Admin Layout ──────────────────────────────────────────────────────────────
function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [navOpen, setNavOpen] = useNavDrawer();

  // Which view they chose, remembered across reloads.
  //
  // This has to persist: without it a refresh drops back to the management view
  // while the URL still points at a recruiting page, so the sidebar shows the
  // admin nav next to recruiting content. Presentation only — the server still
  // honours their real role either way.
  // Held with the id it was read for, so a write can never be attributed to the
  // wrong account during the render where the user has changed but this has not.
  const [viewMode, setViewMode] = React.useState(() => ({
    userId: user?.id ?? null,
    recruiter: readViewMode(user),
  }));

  // Re-read whenever the signed-in account changes.
  React.useEffect(() => {
    const id = user?.id ?? null;
    setViewMode(current => (
      current.userId === id ? current : { userId: id, recruiter: readViewMode(user) }
    ));
  }, [user]);

  React.useEffect(() => {
    const key = viewModeKeyFor(user);
    // Skip the commit where the user has changed but viewMode still holds the
    // previous account's value — writing it here would carry the choice across.
    if (!key || viewMode.userId !== user?.id) return;
    try {
      localStorage.setItem(key, viewMode.recruiter ? 'recruiter' : 'management');
    } catch { /* storage unavailable; the choice just will not survive a reload */ }
  }, [viewMode, user]);

  // Drop the pre-existing shared key once, so browsers carrying one from before
  // this change stop applying it to whoever signs in next.
  React.useEffect(() => {
    try { localStorage.removeItem(VIEW_MODE_KEY); } catch { /* nothing to do */ }
  }, []);

  const setPreviewAsRecruiter = React.useCallback((next) => {
    setViewMode(current => ({
      ...current,
      recruiter: typeof next === 'function' ? next(current.recruiter) : next,
    }));
  }, []);

  // A platform account has no recruiting workspace, so the preference means
  // nothing for them — honouring it would only drop them onto company pages
  // they cannot open.
  const previewAsRecruiter = user?.company_id ? viewMode.recruiter : false;

  // A company admin, manager, team lead, and recruiter share one sidebar.
  // Only a super admin still has a separate platform view.
  const managementMode = isSuperAdmin(user) && !previewAsRecruiter;
  const showTeam       = canManageTeam(user) && !managementMode && !previewAsRecruiter;
  const canSwitch      = isSuperAdmin(user) && Boolean(user?.company_id);

  const handleLogout = () => {
    // Clear the view choice with the session — it should not outlive the login
    // it was made in, nor greet whoever signs in next on this browser.
    clearViewMode(user);
    logout();
    navigate('/login');
  };

  // A super admin isn't "a recruiter" — switching drops them into their own
  // company's workspace, so name it after that company rather than a role.
  const workspace = user?.company_name;
  const viewLabel = isSuperAdmin(user)
    ? (previewAsRecruiter
        ? { current: workspace || 'Recruiter view', action: '← Back to platform' }
        : { current: 'Platform',
            // With no company of their own there is nothing to switch *to* —
            // it is a preview of the recruiter UI, not their own workspace.
            action: workspace ? `Switch to ${workspace} →` : 'Preview recruiter view →' })
    : (previewAsRecruiter
        ? { current: 'Recruiting',      action: '← Back to team management' }
        : { current: 'Team management', action: 'Switch to recruiting →' });

  // Management views replace the recruiting nav rather than extending it.
  const recruitingNav = NAV_ITEMS.filter(item => item.path !== '/settings');
  const navItems = managementMode
    ? PLATFORM_NAV_ITEMS
    : [
        ...(user?.company_id ? recruitingNav : []),
        ...(showTeam ? TEAM_NAV_ITEMS : []),
        ...(isAdmin(user) ? [
          ...(SHOW_JOB_COSTS ? [{ path: '/job-costs', label: 'Cost by job', icon: Receipt }] : []),
          { path: '/manage-wallet', label: 'Manage Wallet', icon: ClipboardList },
        ] : []),
        ...(user?.company_id ? NAV_ITEMS.filter(item => item.path === '/settings') : []),
      ];

  return (
    <ViewModeContext.Provider value={{ recruiterView: previewAsRecruiter, setRecruiterView: setPreviewAsRecruiter }}>
    <ImpersonationBanner />
    <div className="app-layout">
      {/* Narrow-viewport header. Hidden by CSS wherever the sidebar is permanent. */}
      <div className="mobile-topbar">
        <button
          type="button"
          className="hamburger-btn"
          onClick={() => setNavOpen(true)}
          aria-label="Open navigation menu"
          aria-expanded={navOpen}
          aria-controls="app-sidebar"
        >
          <Menu size={20} strokeWidth={2} />
        </button>
        <span className="brand-icon">⬡</span>
        <span className="brand-name">Recruiter24x7</span>
      </div>

      {navOpen && (
        <div
          className="sidebar-backdrop"
          onClick={() => setNavOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside id="app-sidebar" className={`sidebar${navOpen ? ' sidebar-open' : ''}`}>
        <div className="sidebar-brand">
          <BrandMark size={30} />
          <div>
            <BrandWordmark size={16} />
            <div className="brand-sub">by Amartam Solutions</div>
          </div>
          <button
            type="button"
            className="sidebar-close"
            onClick={() => setNavOpen(false)}
            aria-label="Close navigation menu"
          >
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        <nav className="sidebar-nav">
          {navItems.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              /* Tapping the page you're already on doesn't change the route, so
                 the drawer wouldn't close on its own. */
              onClick={() => setNavOpen(false)}
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
            >
              <span className="nav-icon"><item.icon size={16} strokeWidth={1.9} /></span>
              {item.label}
            </NavLink>
          ))}
        </nav>

        {canSwitch && (
          <div style={{ padding: '0 12px 12px' }}>
            <div style={{
              fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
              color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6,
            }}>
              Viewing as
            </div>
            <button
              type="button"
              onClick={() => {
                // Always return to the root, otherwise the page we're on can
                // vanish from the nav while staying on screen.
                setPreviewAsRecruiter(v => !v);
                setNavOpen(false);
                navigate('/');
              }}
              title="Changes what you see, not what you can do — your permissions are unchanged"
              style={{
                width: '100%', padding: '9px 11px', borderRadius: 8,
                cursor: 'pointer', textAlign: 'left', lineHeight: 1.35,
                background: previewAsRecruiter ? 'rgba(245,158,11,0.14)' : 'rgba(88,166,255,0.10)',
                border: `1px solid ${previewAsRecruiter ? 'rgba(245,158,11,0.5)' : 'rgba(88,166,255,0.35)'}`,
                color: previewAsRecruiter ? '#f59e0b' : 'inherit',
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                {previewAsRecruiter
                  ? <CircleDot size={13} strokeWidth={2} style={{ flexShrink: 0 }} />
                  : <Circle size={13} strokeWidth={2} style={{ flexShrink: 0 }} />}
                {viewLabel.current}
              </div>
              <div style={{ fontSize: 10, opacity: 0.8, marginTop: 1 }}>
                {viewLabel.action}
              </div>
            </button>
          </div>
        )}

        <div className="sidebar-footer">
          {/* Candidate portal hidden — candidates join from email meeting links only
          <a
            href="/portal"
            target="_blank"
            rel="noreferrer"
            className="nav-item"
            style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8 }}
          >
            <span className="nav-icon"><Globe size={16} strokeWidth={1.9} /></span>
            Candidate Portal
          </a>
          */}

          {/* Company info & logout */}
          {user ? (
            <div style={{
              marginTop: 12,
              padding: '10px 12px',
              background: 'rgba(88,166,255,0.06)',
              border: '1px solid rgba(88,166,255,0.15)',
              borderRadius: 10,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <CompanyAvatar user={user} />
                <div style={{ minWidth: 0 }}>
                  <div style={{
                    fontSize: 12, fontWeight: 600, color: 'var(--text-primary)',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {user.name || 'Company'}
                  </div>
                  <div style={{
                    fontSize: 10, color: 'var(--text-muted)',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {user.email}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                    {ROLE_LABELS[user.role] || user.role}
                    {/* A super admin spans every company, so naming one beside
                        their role only misleads — unless they're inside it. */}
                    {isSuperAdmin(user)
                      ? (previewAsRecruiter && user.company_name ? ` · ${user.company_name}` : ' · All companies')
                      : (user.company_name ? ` · ${user.company_name}` : '')}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => { setNavOpen(false); navigate('/profile'); }}
                style={{
                  width: '100%', padding: '5px 0', marginBottom: 6,
                  background: 'transparent',
                  border: '1px solid var(--border)',
                  borderRadius: 6, fontSize: 11, fontWeight: 600,
                  color: 'var(--text-primary)', cursor: 'pointer',
                }}
              >
                Update profile
              </button>
              <button
                type="button"
                onClick={() => { setNavOpen(false); navigate('/change-password'); }}
                style={{
                  width: '100%', padding: '5px 0', marginBottom: 6,
                  background: 'transparent',
                  border: '1px solid var(--border)',
                  borderRadius: 6, fontSize: 11, fontWeight: 600,
                  color: 'var(--text-primary)', cursor: 'pointer',
                }}
              >
                Change password
              </button>
              <button
                onClick={handleLogout}
                style={{
                  width: '100%', padding: '5px 0',
                  background: 'transparent',
                  border: '1px solid rgba(239,68,68,0.3)',
                  borderRadius: 6, fontSize: 11, fontWeight: 600,
                  color: 'var(--red)', cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.1)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                Sign Out
              </button>
            </div>
          ) : (
            <div style={{ marginTop: 12 }}>
              <button
                onClick={() => navigate('/login')}
                style={{
                  width: '100%', padding: '8px 0',
                  background: 'rgba(88,166,255,0.1)',
                  border: '1px solid rgba(88,166,255,0.25)',
                  borderRadius: 8, fontSize: 12, fontWeight: 600,
                  color: 'var(--accent)', cursor: 'pointer',
                }}
              >
                Sign In
              </button>
            </div>
          )}

        </div>
      </aside>

      {user?.company_id ? <ChatWidget /> : null}

      <main className="main-content">
        <Routes>
          {/* A super admin gets the platform overview at the root; everyone
              else gets the recruiting dashboard. */}
          <Route path="/"                          element={<HomeRoute />} />
          <Route path="/jobs"                      element={<CompanyRequiredRoute><JobsList /></CompanyRequiredRoute>} />
          <Route path="/jobs/new"                  element={<CompanyRequiredRoute><JobCreatorRoute><CreateJob /></JobCreatorRoute></CompanyRequiredRoute>} />
          <Route path="/jobs/:id"                  element={<CompanyRequiredRoute><JobDetail /></CompanyRequiredRoute>} />
          <Route path="/candidates"                element={<CompanyRequiredRoute><CandidatesList /></CompanyRequiredRoute>} />
          <Route path="/candidates/:id"            element={<CompanyRequiredRoute><CandidateDetail /></CompanyRequiredRoute>} />
          {/* Hidden for now — recruiter text / video interview rooms
          <Route path="/interview/:candidateId"    element={<CompanyRequiredRoute><InterviewRoom /></CompanyRequiredRoute>} />
          <Route path="/video-interview/:candidateId" element={<CompanyRequiredRoute><VideoInterview /></CompanyRequiredRoute>} />
          */}
          <Route path="/shortlist"                 element={<CompanyRequiredRoute><Shortlist /></CompanyRequiredRoute>} />
          <Route path="/shortlist/:jobId"          element={<CompanyRequiredRoute><Shortlist /></CompanyRequiredRoute>} />
          <Route path="/usage"                     element={<CompanyRequiredRoute><UsageCost /></CompanyRequiredRoute>} />
          <Route path="/settings"                  element={<CompanyRequiredRoute><Settings /></CompanyRequiredRoute>} />
          <Route path="/profile"                  element={<Profile />} />
          <Route path="/change-password"          element={<ChangePassword />} />
          <Route path="/recharge-orders" element={<Navigate to="/manage-wallet" replace />} />
          <Route
            path="/manage-wallet"
            element={
              <RoleRoute allow={isAdmin} label="Only a company admin can view recharge orders.">
                <RechargeOrders />
              </RoleRoute>
            }
          />
          <Route
            path="/job-costs"
            element={
              <RoleRoute allow={isAdmin} label="Only a company admin can view job costs.">
                <JobCosts />
              </RoleRoute>
            }
          />
          <Route
            path="/team"
            element={
              <RoleRoute allow={canManageTeam} label="Only an admin, manager or team lead can manage a team.">
                <Team />
              </RoleRoute>
            }
          />
          <Route
            path="/platform/companies"
            element={
              <RoleRoute allow={isSuperAdmin} label="Only a super admin can manage customer companies.">
                <Companies />
              </RoleRoute>
            }
          />
          <Route
            path="/platform/super-admins"
            element={
              <RoleRoute allow={isSuperAdmin} label="Only a super admin can view platform owners.">
                <SuperAdmins />
              </RoleRoute>
            }
          />
          <Route
            path="/platform/wallet-report"
            element={
              <RoleRoute allow={isSuperAdmin} label="Only a super admin can view wallet profit.">
                <WalletReport />
              </RoleRoute>
            }
          />
          <Route
            path="/platform/ai-charges"
            element={
              <RoleRoute allow={isSuperAdmin} label="Only a super admin can view AI charges.">
                <AiCharges />
              </RoleRoute>
            }
          />
          <Route
            path="/platform/settings"
            element={
              <RoleRoute allow={isSuperAdmin} label="Only a super admin can change platform settings.">
                <PlatformSettings />
              </RoleRoute>
            }
          />
          <Route
            path="/platform/demo-requests"
            element={
              <RoleRoute allow={isSuperAdmin} label="Only a super admin can view demo requests.">
                <DemoRequests />
              </RoleRoute>
            }
          />
        </Routes>
      </main>
    </div>
    </ViewModeContext.Provider>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <AlertProvider>
    <AuthProvider>
      <Router>
        <LowBalanceModal />
        <Routes>
          {/* Public auth routes */}
          <Route path="/login"            element={<Login />} />
          <Route path="/auth/callback"    element={<AuthCallback />} />

          {/* Candidate-facing pages: tokenized email links only. No portal browse/self-schedule. */}
          {/* <Route path="/portal"                          element={<PortalJobs />} /> */}
          {/* Public job board, loaded in an iframe on a customer's own website. */}
          {/* Opens in a new tab; mints a session scoped to that tab only. */}
          <Route path="/impersonate/:companyId"          element={<ImpersonateHandoff />} />
          <Route path="/embed/jobs/:token"               element={<EmbedJobBoard />} />
          {/* Short public link for one job, for sharing anywhere. */}
          <Route path="/j/:code"                        element={<SharedJob />} />
          <Route path="/portal/apply/:jobId"             element={<PortalApply />} />
          <Route path="/portal/interview/:token"         element={<PortalInterview />} />
          <Route path="/interview/meeting/:token/thank-you" element={<InterviewThankYou />} />
          <Route path="/portal/meeting/:token/thank-you"    element={<InterviewThankYou />} />
          <Route path="/interview/meeting/:token"        element={<PortalMeeting />} />
          <Route path="/portal/meeting/:token"           element={<PortalMeeting />} />
          <Route path="/stt-lab"                         element={<SttLab />} />
          <Route path="/code-lab"                        element={<CodeLab />} />
          {/* <Route path="/portal/schedule/:candidateId"    element={<PortalSelfSchedule />} /> */}

          {/* Admin routes — require login */}
          <Route path="/*" element={<ProtectedRoute><RefreshProvider><AdminLayout /></RefreshProvider></ProtectedRoute>} />
        </Routes>
      </Router>
    </AuthProvider>
    </AlertProvider>
  );
}
